import React, { ReactNode, useRef, useEffect, useState } from 'react';

interface ScrollContainerProps {
  children: ReactNode;
}

const ScrollContainer: React.FC<ScrollContainerProps> = ({ children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const [showScrollbar, setShowScrollbar] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollWidth, clientWidth } = contentRef.current;
        setShowScrollbar(scrollWidth > clientWidth);
      }
    };

    const handleContentScroll = (e: Event) => {
      if (scrollbarRef.current && e.target instanceof HTMLElement) {
        scrollbarRef.current.scrollLeft = e.target.scrollLeft;
      }
    };

    const handleScrollbarScroll = (e: Event) => {
      if (contentRef.current && e.target instanceof HTMLElement) {
        contentRef.current.scrollLeft = e.target.scrollLeft;
      }
    };

    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;

    checkScroll();
    window.addEventListener('resize', checkScroll);
    content?.addEventListener('scroll', handleContentScroll);
    scrollbar?.addEventListener('scroll', handleScrollbarScroll);

    const observer = new ResizeObserver(checkScroll);
    if (content) {
      observer.observe(content);
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      content?.removeEventListener('scroll', handleContentScroll);
      scrollbar?.removeEventListener('scroll', handleScrollbarScroll);
      if (content) {
        observer.unobserve(content);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div className="relative flex flex-col h-[calc(100vh-4rem)]">
      <div 
        ref={contentRef}
        className="flex-1 overflow-x-auto overflow-y-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1'
        }}
      >
        {children}
      </div>
      {showScrollbar && (
        <div 
          className="sticky bottom-0 left-0 right-0 h-3 bg-white shadow-[0_-1px_3px_rgba(0,0,0,0.1)] z-50"
        >
          <div 
            ref={scrollbarRef}
            className="h-full overflow-x-auto custom-scrollbar"
          >
            <div 
              className="h-full" 
              style={{ 
                width: contentRef.current?.scrollWidth,
                minWidth: '100%'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrollContainer;